/**
 * Building blocks shared by the Operations pages: localised status vocabularies,
 * refresh/link buttons, relative times, JSON payload blocks and a polling hook that
 * keeps the previous data on screen while it reloads.
 */

import { BellOff, BookOpen, ChevronRight, ExternalLink, RefreshCw, Siren } from 'lucide-react';
import { useCallback, useEffect, useState, type ComponentProps, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { isAbortError, type AlertmanagerAlert, type Incident } from '../../api';
import { Badge, type Tone } from '../../components/Badge';
import { DataTable, type Column } from '../../components/blocks';
import { EntityRef } from '../../components/EntityRef';
import { formatDateTime, formatRelative } from '../../lib/format';

// ---------------------------------------------------------------------------
// Vocabularies (labels come from the `ops` namespace; the raw code goes in `title`)

export type EnumGroup =
  | 'severity'
  | 'incidentStatus'
  | 'workflowState'
  | 'stepOutcome'
  | 'commandState'
  | 'auditOutcome'
  | 'eventStatus'
  | 'alertState'
  | 'gap';

export const SEVERITY_TONE: Record<string, Tone> = { P0: 'crit', P1: 'serious', P2: 'warn', P3: 'info' };
export const SEVERITY_ORDER = ['P0', 'P1', 'P2', 'P3'] as const;

export const INCIDENT_TONE: Record<string, Tone> = { OPEN: 'crit', ACKNOWLEDGED: 'serious', MITIGATING: 'warn', MONITORING: 'pending', RESOLVED: 'ok' };

export const WORKFLOW_TONE: Record<string, Tone> = {
  PENDING: 'pending',
  RUNNING: 'pending',
  WAITING: 'pending',
  RETRYING: 'warn',
  COMPLETED: 'ok',
  FAILED: 'crit',
  MANUAL_REVIEW: 'crit',
  CANCELLED: 'neutral',
  COMPENSATING: 'warn',
};

export const STEP_TONE: Record<string, Tone> = { STARTED: 'pending', COMPLETED: 'ok', FAILED: 'crit', RETRY: 'warn', WAITING: 'pending', UNKNOWN: 'serious', SKIPPED: 'neutral' };

export const COMMAND_TONE: Record<string, Tone> = {
  PENDING: 'pending',
  SENT: 'pending',
  SUCCESS: 'ok',
  REJECTED: 'crit',
  UNKNOWN: 'serious',
  RECONCILING: 'serious',
  RETRY_PENDING: 'warn',
  MANUAL_REVIEW: 'crit',
};

export const GAP_TONE: Record<string, Tone> = {
  MISSING_DOSSIER: 'crit',
  INCOMPLETE_DOSSIER: 'warn',
  VERIFICATION_NOT_VALID: 'serious',
  SUBJECT_MISMATCH: 'serious',
  VNNIC_SYNC_MISSING: 'crit',
  VNNIC_SYNC_PENDING: 'pending',
  VNNIC_SYNC_REJECTED: 'serious',
  VNNIC_SYNC_ERROR: 'crit',
  REPORT_PROJECTION_MISSING: 'warn',
  VNNIC_REGISTRY_MISSING: 'crit',
};

/** `PENDING_REVIEW` / `DomainRenewalWorkflow` -> `Pending review` / `Domain renewal workflow`. */
export function humanize(value: string): string {
  const spaced = /[_.]/.test(value) ? value.replace(/[_.]+/g, ' ') : value.replace(/([a-z\d])([A-Z])/g, '$1 $2');
  const s = spaced.toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Label for an enum value; unknown values fall back to the humanised code. */
export function useEnumLabel(): (group: EnumGroup, value: string) => string {
  const { t, i18n } = useTranslation('ops');
  return useCallback(
    (group: EnumGroup, value: string) => {
      const exact = `ops:${group}.${value}`;
      const upper = `ops:${group}.${value.toUpperCase()}`;
      const key = i18n.exists(exact) ? exact : i18n.exists(upper) ? upper : null;
      // Dynamic keys: checked with `exists` above, so the cast is safe.
      return key ? (t as unknown as (k: string) => string)(key) : humanize(value);
    },
    // `i18n.language` re-creates the function after a language switch.
    // eslint-disable-next-line react/exhaustive-deps
    [t, i18n, i18n.language],
  );
}

/** Localised status badge; the raw code stays available as a tooltip. */
export function EnumBadge({ group, value, tones, icon }: { group: EnumGroup; value: string | null | undefined; tones?: Record<string, Tone>; icon?: boolean }) {
  const label = useEnumLabel();
  if (!value) return <Badge tone="neutral">—</Badge>;
  const tone = tones?.[value] ?? tones?.[value.toUpperCase()] ?? 'neutral';
  return (
    <Badge tone={tone} title={value} icon={icon}>
      {label(group, value)}
    </Badge>
  );
}

export function SeverityBadge({ severity, withName = false }: { severity: string; withName?: boolean }) {
  const label = useEnumLabel();
  const known = severity in SEVERITY_TONE;
  return (
    <Badge tone={SEVERITY_TONE[severity] ?? 'neutral'} title={known ? `${severity} · ${label('severity', severity)}` : severity}>
      {severity}
      {withName && known ? ` ${label('severity', severity)}` : ''}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Small UI pieces

export function RefreshButton({ onClick, busy = false, size = 'sm' }: { onClick: () => void; busy?: boolean; size?: 'sm' | 'default' }) {
  const { t } = useTranslation();
  return (
    <Button type="button" variant="outline" size={size} onClick={onClick} disabled={busy}>
      <RefreshCw className={cn(busy && 'animate-spin')} aria-hidden="true" />
      {t('action.refresh')}
    </Button>
  );
}

/** A react-router link styled as a button. */
export function LinkButton({ to, children, variant = 'outline', size = 'sm', className }: { to: string; children: ReactNode; variant?: ComponentProps<typeof Button>['variant']; size?: ComponentProps<typeof Button>['size']; className?: string }) {
  return (
    <Button asChild variant={variant} size={size} className={className}>
      <Link to={to}>{children}</Link>
    </Button>
  );
}

/** Relative time with the exact UTC timestamp as a tooltip. */
export function Since({ at, className }: { at: string | null | undefined; className?: string }) {
  if (!at) return <span className="text-muted-foreground">—</span>;
  return (
    <time dateTime={at} title={formatDateTime(at)} className={cn('whitespace-nowrap', className)}>
      {formatRelative(at)}
    </time>
  );
}

/** Label/value chips such as `service=api`. */
export function CodeChips({ items, className }: { items: readonly string[]; className?: string }) {
  if (items.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <span className={cn('flex flex-wrap gap-1', className)}>
      {items.map((s) => (
        <code key={s} className="rounded bg-muted px-1.5 py-0.5 text-xs break-all">
          {s}
        </code>
      ))}
    </span>
  );
}

/** Collapsible, pretty-printed JSON. `undefined` means withheld by the server, `null` means empty. */
export function JsonBlock({ label, value, open = false }: { label: string; value: unknown; open?: boolean }) {
  const { t } = useTranslation('ops');
  if (value === undefined) return <p className="text-sm text-muted-foreground">{t('shared.withheld', { label })}</p>;
  if (value === null) return <p className="text-sm text-muted-foreground">{t('shared.empty', { label })}</p>;
  return (
    <details className="group rounded-lg border bg-muted/30" open={open}>
      <summary className="flex cursor-pointer list-none items-center gap-1.5 px-3 py-2 text-sm font-medium select-none [&::-webkit-details-marker]:hidden">
        <ChevronRight className="size-4 text-muted-foreground transition-transform group-open:rotate-90" aria-hidden="true" />
        {label}
      </summary>
      <pre className="max-h-[28rem] overflow-auto border-t px-3 py-2 font-mono text-xs leading-relaxed">{typeof value === 'string' ? value : JSON.stringify(value, null, 2)}</pre>
    </details>
  );
}

/** Small uppercase heading for a group of tiles. */
export function GroupHeading({ icon: Icon, title, hint }: { icon?: typeof RefreshCw; title: ReactNode; hint?: ReactNode }) {
  return (
    <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
      <h2 className="flex items-center gap-2 text-sm font-semibold tracking-wide text-foreground/80 uppercase">
        {Icon ? <Icon className="size-4 text-muted-foreground" aria-hidden="true" /> : null}
        {title}
      </h2>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

/** Muted footnote text below a page. */
export function Footnote({ children }: { children: ReactNode }) {
  return <p className="pt-6 text-xs leading-relaxed text-muted-foreground">{children}</p>;
}

// ---------------------------------------------------------------------------
// Incidents (shared by the incident list, one alert and one service)

export const incidentPath = (id: string): string => `/ops/incidents/${encodeURIComponent(id)}`;

/** Incident rows; every row opens the incident. */
export function IncidentTable({ rows, empty }: { rows: readonly Incident[]; empty: ReactNode }) {
  const { t } = useTranslation('ops');
  const navigate = useNavigate();
  const columns: Column<Incident>[] = [
    { key: 'sev', header: t('incidents.col.severity'), cell: (i) => <SeverityBadge severity={i.severity} /> },
    {
      key: 'title',
      header: t('incidents.col.title'),
      cell: (i) => <EntityRef label={i.title} to={incidentPath(i.id)} id={i.id} />,
    },
    { key: 'status', header: t('incidents.col.status'), cell: (i) => <EnumBadge group="incidentStatus" value={i.status} tones={INCIDENT_TONE} /> },
    {
      key: 'owner',
      header: t('incidents.col.owner'),
      // Not a link: the whole row opens the incident, and `DataTable` would swallow a link pointing elsewhere.
      cell: (i) => <EntityRef label={i.owner_name} sub={i.owner_email} id={i.owner} />,
    },
    { key: 'started', header: t('incidents.col.started'), cell: (i) => <Since at={i.started_at} /> },
  ];
  return <DataTable columns={columns} rows={rows} rowKey={(i) => i.id} onRowClick={(i) => navigate(incidentPath(i.id))} empty={empty} />;
}

// ---------------------------------------------------------------------------
// Alerts (shared by the alert list, one alert, one service and one incident)

export const severityOf = (a: AlertmanagerAlert): string => a.labels.severity ?? 'P3';

/** Page for everything firing under one alert name. */
export const alertLink = (name: string): string => `/ops/alerts/${encodeURIComponent(name)}`;

/** Opens the "new incident" dialog pre-filled from an alert. */
export function incidentLink(a: AlertmanagerAlert): string {
  const sev = severityOf(a);
  const p = new URLSearchParams({
    new: '1',
    title: a.annotations.summary ?? a.labels.alertname ?? 'Alert',
    severity: sev in SEVERITY_TONE ? sev : 'P2',
    alert: a.labels.alertname ?? '',
  });
  if (a.labels.service) p.set('service', a.labels.service);
  return `/ops/incidents?${p.toString()}`;
}

/** Runbook for an alert: a link when the rule carries a URL, the path otherwise. */
export function RunbookLink({ url }: { url: string | undefined }) {
  const { t } = useTranslation('ops');
  if (!url) return null;
  // Rule annotations carry repository-relative runbook paths (observability/runbooks/...).
  if (/^https?:\/\//.test(url)) {
    return (
      <a href={url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
        <BookOpen className="size-4" aria-hidden="true" /> {t('alerts.runbook')} <ExternalLink className="size-3" aria-hidden="true" />
      </a>
    );
  }
  return (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      <BookOpen className="size-3.5" aria-hidden="true" />
      {t('alerts.runbookPath')}
      <code className="rounded bg-muted px-1.5 py-0.5 break-all">observability/{url}</code>
    </span>
  );
}

/** One firing alert as a list row. `linkName` off when the page already is that alert. */
export function AlertRow({ a, linkName = true }: { a: AlertmanagerAlert; linkName?: boolean }) {
  const { t } = useTranslation('ops');
  const label = useEnumLabel();
  const name = a.labels.alertname;
  return (
    <li className="space-y-2 px-5 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <SeverityBadge severity={severityOf(a)} />
        {name && linkName ? (
          <Link to={alertLink(name)} className="font-mono text-sm font-semibold text-primary hover:underline">
            {name}
          </Link>
        ) : (
          <strong className="font-mono text-sm">{name ?? '—'}</strong>
        )}
        {a.status.state !== 'active' ? (
          <Badge tone="neutral" title={a.status.state}>
            <BellOff /> {label('alertState', a.status.state)}
          </Badge>
        ) : null}
        <span className="text-xs text-muted-foreground" title={formatDateTime(a.startsAt)}>
          {t('alerts.since', { ago: formatRelative(a.startsAt) })}
        </span>
      </div>
      {a.annotations.summary ? <p className="text-sm">{a.annotations.summary}</p> : null}
      <CodeChips
        items={Object.entries(a.labels)
          .filter(([k]) => k !== 'alertname' && k !== 'severity')
          .map(([k, v]) => `${k}=${v}`)}
      />
      <div className="flex flex-wrap items-center gap-3 pt-1">
        <LinkButton to={incidentLink(a)}>
          <Siren /> {t('alerts.openIncident')}
        </LinkButton>
        <RunbookLink url={a.annotations.runbook_url} />
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Polling

export interface Live<T> {
  data: T | undefined;
  error: unknown;
  /** A request is in flight (first load or refresh). */
  loading: boolean;
  /** When `data` was last received (epoch ms). */
  at: number | null;
  reload: () => void;
}

/**
 * Loads on mount and whenever `tick` changes, keeping the last good data while a
 * refresh runs or after it fails (so a dashboard never blanks out on a hiccup).
 */
export function useLive<T>(load: (signal: AbortSignal) => Promise<T>, tick: number): Live<T> {
  const [attempt, setAttempt] = useState(0);
  const requestKey = `${tick}/${attempt}`;
  const [state, setState] = useState<{ data: T | undefined; error: unknown; at: number | null; settled: string | null }>({
    data: undefined,
    error: null,
    at: null,
    settled: null,
  });

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) setState({ data, error: null, at: Date.now(), settled: requestKey });
      },
      (error: unknown) => {
        if (!controller.signal.aborted && !isAbortError(error)) setState((prev) => ({ ...prev, error, settled: requestKey }));
      },
    );
    return () => controller.abort();
    // `load` is intentionally excluded: callers pass an inline closure; `requestKey` drives reloads.
    // eslint-disable-next-line react/exhaustive-deps
  }, [requestKey]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { data: state.data, error: state.error, loading: state.settled !== requestKey, at: state.at, reload };
}
